// Standard library imports
import React, { useEffect, useState, useContext } from 'react';
// External library imports
import moment from 'moment-timezone';
import _ from 'lodash';
import { UilInfoCircle } from '@iconscout/react-unicons';
// Internal module imports
import { useHistory } from 'react-router-dom';
import classes from '../Home.module.css';
import { Capitalize } from '../../../utils/stringHelper';
import TableWithHeading from '../../../components/TableWithHeading/TableWithHeading';
import { DashboardService } from '../../../services/DashboardService';
import ThreeStateToggle from '../../../components/Inputs/ThreeStateToggle/ThreeStateToggle';
import Typography from '../../../components/Typography/Typography';
import { AuthContext } from '../../../context/AuthContext';
import { truncateNumber } from '../../../utils/numberHelpers';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import allDeviceCss from '../../../styles/AllDevices.module.css';

const MARKET_TYPE = {
    left: {
        title: 'FCR Markets',
        value: 'left',
    },
    right: {
        title: 'FRR Markets',
        value: 'right',
    },
};

const getEnabledMarkets = (enabledMarkets = []) => {
    const enabledMarketType = {
        fcr_based: false,
        frr_based: false,
    };
    enabledMarkets.map((m) => {
        if (m.includes('FCR') || m.includes('FFR')) {
            enabledMarketType.fcr_based = true;
        } else if (m.includes('FRR')) {
            enabledMarketType.frr_based = true;
        }
    });
    return enabledMarketType;
};

const EventFaultCard = () => {
    const { state } = useContext(AuthContext);
    const history = useHistory();
    const [eventsData, setEventsData] = useState([]);
    const [eventsLoading, setEventsLoading] = useState([]);
    const enabledMarkets = _.get(state, ['user', 'customerId', 'e3DashboardConfig', 'eventMarkets'], []);
    const [enabledMarketType, setEnabledMarketType] = useState(getEnabledMarkets(enabledMarkets));
    const [selectedMarketType, setSelectedMarketType] = useState(enabledMarketType.fcr_based ? 'fcr_based' : 'frr_based');
    const [tableHeadersAndKeys, setTableHeadersAndKeys] = useState({
        headers: ['Type', 'Instance', 'Direction', 'Start Time (CET)', 'Delta Load (%)', 'Duration', 'Consumption (kWh)'],
        keys: ['type', 'instanceName', 'direction', 'startTime', 'processedDeltaLoad', 'duration', 'consumption'],
    });

    useEffect(() => {
        if (selectedMarketType === 'fcr_based') {
            setTableHeadersAndKeys({
                headers: ['Type', 'Instance', 'Direction', 'Start Time (CET)', 'Delta Load (%)', 'Duration', 'Consumption (kWh)'],
                keys: ['type', 'instanceName', 'direction', 'startTime', 'processedDeltaLoad', 'duration', 'consumption'],
            });
        } else {
            setTableHeadersAndKeys({
                headers: ['Type', 'Instance', 'Direction', 'Start Time (CET)', 'Max Setpoint', 'Duration', 'Consumption (kWh)'],
                keys: ['type', 'instanceName', 'direction', 'startTime', 'maxSetPoint', 'duration', 'consumption'],
            });
        }
        getData();
    }, [selectedMarketType]);

    const getData = () => {
        const today = moment().format('YYYY-MM-DD');
        DashboardService.getEventsData(
            { startDate: today, endDate: today, marketType: selectedMarketType },
            () => setEventsLoading(true),
            ({ data }) => getEventsDataSuccess(data),
            handleError,
            () => setEventsLoading(false)
        );
    };

    const getEventsDataSuccess = (data) => {
        const eventsData = data.data.map((event) => {
            return {
                ...event,
                deltaLoad: event.deltaLoad.toLocaleString('da-DK'),
                direction: Capitalize(event.direction),
                processedDeltaLoad:
                    event.direction === 'increased' ? (
                        <div style={{ color: 'darkgreen' }}>{event.deltaLoad.toLocaleString('da-DK')}</div>
                    ) : (
                        <div style={{ color: 'red' }}> {event.deltaLoad.toLocaleString('da-DK')}</div>
                    ),
                consumption: isNaN(event.consumption) ? calculateConsumption(event.dataLog, event.difference) : truncateNumber(event.consumption, 3).toLocaleString('da-DK'),
            };
        });
        setEventsData(eventsData);
    };

    const calculateConsumption = (dataLog = [], duration = 0) => {
        let totalPower = 0;
        let count = dataLog.length;
        let consumption = '--';
        dataLog.map((item) => {
            totalPower += item.power || 0;
        });
        const avgPower = totalPower / count;
        if (avgPower > 0) {
            consumption = avgPower * (duration / 3600);
        }

        return isNaN(consumption) ? (
            consumption
        ) : (
            <span className={classes.EstimatedConsumption}>
                <span style={{ marginBottom: '0.4vh' }}>{truncateNumber(consumption, 3).toLocaleString('da-DK')}</span>
                <CustomTooltip content={'Estimated Consumption'}>
                    <UilInfoCircle
                        size="1.2vw"
                        style={{
                            cursor: 'pointer',
                        }}
                    />
                </CustomTooltip>
            </span>
        );
    };

    const handleError = (error) => {
        console.error(error);
    };

    return (
        <div className={classes.EventContainer}>
            <div className={classes.ToggleHeadingContainer}>
                <div style={{ display: 'flex', gap: '1vw', alignItems: 'center' }}>
                    <Typography content={'Events'} />
                    {eventsData.length > 0 && (
                        <div className={allDeviceCss.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={eventsData.length} />
                            </span>
                        </div>
                    )}
                </div>
                {enabledMarketType.fcr_based && enabledMarketType.frr_based && (
                    <div>
                        <ThreeStateToggle
                            labels={MARKET_TYPE}
                            onChange={(e) => {
                                switch (e) {
                                    case 'left': {
                                        setSelectedMarketType('fcr_based');
                                        break;
                                    }
                                    case 'right': {
                                        setSelectedMarketType('frr_based');
                                        break;
                                    }
                                }
                            }}
                            defaultValue={'left'}
                        />
                    </div>
                )}
            </div>
            <TableWithHeading head={tableHeadersAndKeys.headers} data={eventsData} keys={tableHeadersAndKeys.keys} loading={eventsLoading} />
        </div>
    );
};

export default EventFaultCard;
